import React, { useState, useEffect, useRef } from "react";
import { useBar } from "../../../../context/BarContext";
import GENRES from "../../../../utils/genres";
import BarOwnerArtistCard from "../BarOwnerArtistCard";
import ManagementArtistProfileModal from "../management/ManagementArtistProfileModal";
import { useBarOwnerData } from "../../../../context/BarOwnerDataContext";
import { useUserRole } from "../../../../context/UserRoleContext";

const FindArtists = ({
  onFetchArtists,
  getCachedData,
  activeTab,
  isMapsLoaded = false,
}) => {
  const { selectedBar } = useBar();
  const { userRole } = useUserRole();
  const { invalidateCache, fetchManagementArtists, fetchFindArtists } =
    useBarOwnerData();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGenre, setSelectedGenre] = useState("");
  const [selectedRadius, setSelectedRadius] = useState("25"); // Default 25 miles
  const [selectedArtist, setSelectedArtist] = useState(null);
  const previousActiveTabRef = useRef(null);

  // Reset filters when tab becomes active
  useEffect(() => {
    if (
      activeTab === "find-artists" &&
      previousActiveTabRef.current !== "find-artists"
    ) {
      setSearchQuery("");
      setSelectedGenre("");
      setSelectedRadius("25");
    }
    previousActiveTabRef.current = activeTab;
  }, [activeTab]);

  // Fetch artists when tab becomes active, bar changes, or filters change
  useEffect(() => {
    if (activeTab !== "find-artists" || !selectedBar?.bar_id) return;

    const timeoutId = setTimeout(
      () => {
        // Check if data is cached for current params
        const cached = getCachedData?.(selectedBar.bar_id, {
          search: searchQuery,
          genre: selectedGenre,
          radius: selectedRadius,
        });

        // Fetch once per unique filter combo; empty results are valid and should not refetch-loop
        if (!cached?.loading && !cached?.hasFetched) {
          onFetchArtists?.(selectedBar.bar_id, {
            search: searchQuery,
            genre: selectedGenre,
            radius: selectedRadius,
          });
        }
      },
      searchQuery ? 500 : 0
    ); // Debounce only for search input

    return () => clearTimeout(timeoutId);
  }, [
    activeTab,
    selectedBar?.bar_id,
    searchQuery,
    selectedGenre,
    selectedRadius,
    getCachedData,
    onFetchArtists,
  ]);

  // Get cached data based on current filters
  const cachedData = getCachedData
    ? getCachedData(selectedBar?.bar_id, {
        search: searchQuery,
        genre: selectedGenre,
        radius: selectedRadius,
      })
    : { artists: [], loading: false, error: null, hasFetched: false };

  const artists = cachedData.artists || [];
  const loading = cachedData.loading || false;
  const error = cachedData.error || null;

  // Mile range options
  const mileRanges = [
    { label: "5 miles", value: "5" },
    { label: "10 miles", value: "10" },
    { label: "25 miles", value: "25" },
    { label: "50 miles", value: "50" },
    { label: "100 miles", value: "100" },
    { label: "500 miles", value: "500" },
    { label: "4000 miles", value: "4000" },
  ];

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedGenre("");
    setSelectedRadius("25"); // Reset to default
  };

  const hasActiveFilters =
    searchQuery.trim() || selectedGenre || selectedRadius !== "25";

  const handleArtistClick = (artist) => {
    setSelectedArtist(artist);
  };

  if (!selectedBar) {
    return (
      <div className="artists-content">
        <div className="no-bar-message">
          <h3>No Bar Selected</h3>
          <p>Please select a bar to search for artists.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="artists-content">
      {/* Search Section */}
      <div className="search-section">
        <div className="search-bar-container">
          <input
            type="text"
            placeholder="Search artists by name or location..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        {/* Filter Section */}
        <div className="filters-section">
          <div className="filters-grid">
            {/* Search Radius Filter */}
            <div className="filter-group">
              <label className="filter-label">Search Radius</label>
              <select
                value={selectedRadius}
                onChange={(e) => setSelectedRadius(e.target.value)}
                className="filter-select"
              >
                {mileRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Genre Filter */}
            <div className="filter-group">
              <label className="filter-label">Genre</label>
              <select
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
                className="filter-select"
              >
                <option value="">All Genres</option>
                {GENRES.map((genre) => (
                  <option key={genre} value={genre}>
                    {genre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="filter-actions">
              <button onClick={clearFilters} className="clear-filters-btn">
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div
          className="error-message"
          style={{
            padding: "1rem",
            background: "rgba(248, 113, 113, 0.1)",
            border: "1px solid rgba(248, 113, 113, 0.3)",
            borderRadius: "8px",
            marginBottom: "1rem",
            color: "#f87171",
          }}
        >
          {error}
        </div>
      )}

      {/* Results Count */}
      <div className="results-info">
        <span className="results-count">
          {loading ? (
            "Loading..."
          ) : (
            <>
              {artists.length} artist
              {artists.length !== 1 ? "s" : ""} found
              {hasActiveFilters && " (filtered)"}
            </>
          )}
        </span>
      </div>

      {/* Artists Grid */}
      <div className="bars-grid bars-grid--artists">
        {loading ? (
          <div
            className="chat-messages-loading"
            style={{
              gridColumn: "1 / -1",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "3rem",
              minHeight: "400px",
              width: "100%",
            }}
          >
            <div className="spinner" aria-hidden="true" />
            <p
              style={{
                color: "var(--text-secondary)",
                margin: 0,
                marginTop: "1rem",
              }}
            >
              Loading artists…
            </p>
          </div>
        ) : artists.length > 0 ? (
          artists.map((artist) => (
            <BarOwnerArtistCard
              key={artist.artist_id}
              artist={artist}
              onClick={() => handleArtistClick(artist)}
              hasPending={false}
            />
          ))
        ) : (
          <div className="no-results" style={{ gridColumn: "1 / -1" }}>
            <div className="no-results-icon">🔍</div>
            <h3>No artists found</h3>
            <p>
              {hasActiveFilters
                ? "Try adjusting your filters or search terms"
                : "No artists available in this area"}
            </p>
          </div>
        )}
      </div>

      {selectedArtist && (
        <ManagementArtistProfileModal
          isOpen={!!selectedArtist}
          onClose={() => setSelectedArtist(null)}
          artist={selectedArtist}
          associations={[]}
          selectedBar={selectedBar}
          isMapsLoaded={isMapsLoaded}
          onRefresh={() => {
            invalidateCache("managementArtists");
            invalidateCache("findArtists");
            fetchManagementArtists();
            fetchFindArtists(selectedBar?.bar_id, {
              search: searchQuery,
              genre: selectedGenre,
              radius: selectedRadius,
            });
          }}
          manageBarSelection={
            userRole === "bar_admin" ? "context" : "all"
          }
          contextBarId={selectedBar?.bar_id}
        />
      )}
    </div>
  );
};

export default FindArtists;
